import { getLocalTimeZone } from "@internationalized/date";
import { useControlledState } from "@react-stately/utils";
import { Calendar as CalendarIcon } from "@untitledui/icons";
import { useEffect, useMemo, useState } from "react";
import { useDateFormatter } from "react-aria";
import type { DateRangePickerProps as AriaDateRangePickerProps, DateValue } from "react-aria-components";
import { useLocale } from "react-aria-components";
import { Button } from "@/components/base/buttons/button";
import { cx } from "@/utils/cx";
import { generateDateRangePresets } from "@/utils/datePresets";
import { DateRangeCalendarPopover, type DateRangePreset } from "./date-range-calendar-popover";

interface DateRangePickerProps extends AriaDateRangePickerProps<DateValue> {
  /** The function to call when the apply button is clicked. */
  onApply?: () => void;
  /** The function to call when the cancel button is clicked. */
  onCancel?: () => void;
  /** The function to call when the reset button is clicked */
  onReset?: () => void;
  /** Text for the reset button */
  resetText?: string;
  /** The currently selected/applied date range */
  selected: { start: DateValue; end: DateValue } | null;
  /** Whether the button is in active state */
  isActive: boolean;
  /** Custom text class for button */
  textClassName?: string;
  /** Custom class for button */
  buttonClassName?: string;
  /** Whether to show the preset sidebar on desktop. */
  showPresets?: boolean;
  /** Date limits for filtering available presets */
  limits?: { minDate: DateValue; maxDate: DateValue };
}

export const DateRangePicker = ({
  value: valueProp,
  defaultValue,
  selected,
  onChange,
  onApply,
  isActive = false,
  onCancel,
  onReset,
  resetText = "Reset to latest week",
  textClassName,
  buttonClassName,
  showPresets = false,
  limits,
  minValue,
  maxValue,
  ...props
}: DateRangePickerProps) => {
  const { locale } = useLocale();
  const formatter = useDateFormatter({
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const [value, setValue] = useControlledState(valueProp, defaultValue || null, onChange);
  const [selectedPresetKey, setSelectedPresetKey] = useState<string | null>(null);

  const formattedStartDate = selected?.start
    ? formatter.format(selected.start.toDate(getLocalTimeZone()))
    : "Select date";
  const formattedEndDate = selected?.end ? formatter.format(selected.end.toDate(getLocalTimeZone())) : "Select date";

  const presets: Record<string, DateRangePreset> = useMemo(() => {
    return generateDateRangePresets(locale, limits, formatter);
  }, [locale, limits, formatter]);

  // Wrap setValue to clear preset when manually selecting dates
  const handleValueChange = (newValue: { start: DateValue; end: DateValue } | null) => {
    setValue(newValue);

    // Check if the new value matches any preset
    if (newValue) {
      const matchingPreset = Object.entries(presets).find(
        ([_, preset]) =>
          preset.value.start.compare(newValue.start) === 0 && preset.value.end.compare(newValue.end) === 0,
      );

      if (matchingPreset) {
        setSelectedPresetKey(matchingPreset[0]);
      } else {
        setSelectedPresetKey(null);
      }
    } else {
      setSelectedPresetKey(null);
    }
  };

  // Sync selectedPresetKey when selected changes from parent
  useEffect(() => {
    if (selected) {
      const matchingPreset = Object.entries(presets).find(
        ([_, preset]) =>
          preset.value.start.compare(selected.start) === 0 && preset.value.end.compare(selected.end) === 0,
      );

      if (matchingPreset) {
        setSelectedPresetKey(matchingPreset[0]);
      } else {
        setSelectedPresetKey(null);
      }
    } else {
      setSelectedPresetKey(null);
    }
  }, [selected, presets]);

  return (
    <DateRangeCalendarPopover
      value={value}
      onChange={handleValueChange}
      onApply={onApply}
      onCancel={onCancel}
      onReset={onReset}
      resetText={resetText}
      minValue={minValue ?? limits?.minDate}
      maxValue={maxValue ?? limits?.maxDate}
      limits={limits}
      showPresets={showPresets}
      presets={showPresets ? presets : undefined}
      selectedPresetKey={selectedPresetKey}
      onPresetSelect={(key, preset) => {
        setValue(preset.value);
        setSelectedPresetKey(key);
      }}
      {...props}
    >
      <Button
        size="sm"
        color={isActive ? "tertiary" : "secondary"}
        iconLeading={<CalendarIcon className={cx("size-5  text-placeholder", isActive && "text-brand-tertiary")} />}
        className={cx(
          textClassName ? textClassName : "text-xs font-medium",
          isActive && "bg-brand-primary text-brand-primary ring-1 hover:bg-brand-secondary ring-inset ring-brand",
          buttonClassName,
        )}
      >
        {!value ? (
          <span className="text-placeholder">Select dates</span>
        ) : selectedPresetKey && presets[selectedPresetKey as keyof typeof presets] ? (
          presets[selectedPresetKey as keyof typeof presets].label
        ) : (
          `${formattedStartDate} – ${formattedEndDate}`
        )}
      </Button>
    </DateRangeCalendarPopover>
  );
};
